import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { CameraViewMode, CameraSource } from './types';

interface CameraLayoutContextValue {
  viewMode: CameraViewMode;
  setViewMode: (mode: CameraViewMode) => void;
  cameraSource: CameraSource | null;
  setCameraSource: (source: CameraSource | null) => void;
  reconnect: () => void;
  isReconnecting: boolean;
  registerReconnect: (fn: () => void, isReconnecting: boolean) => void;
  collapsed: boolean;
  toggleCollapse: () => void;
}

const CameraLayoutContext = createContext<CameraLayoutContextValue>({
  viewMode: 'default',
  setViewMode: () => {},
  cameraSource: null,
  setCameraSource: () => {},
  reconnect: () => {},
  isReconnecting: false,
  registerReconnect: () => {},
  collapsed: false,
  toggleCollapse: () => {},
});

export const useCameraLayout = () => useContext(CameraLayoutContext);

export const CameraLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [viewMode, setViewModeState] = useState<CameraViewMode>('default');
  const [cameraSource, setCameraSourceState] = useState<CameraSource | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const reconnectRef = useRef<() => void>(() => {});

  const setViewMode = useCallback((mode: CameraViewMode) => {
    setViewModeState(prev => (prev === mode ? 'default' : mode));
  }, []);

  const setCameraSource = useCallback((source: CameraSource | null) => {
    setCameraSourceState(prev => {
      if (!source && !prev) return prev;
      if (!source || !prev) return source;
      if (
        prev.serialNumber === source.serialNumber &&
        prev.isViewer === source.isViewer &&
        prev.isEnRoute === source.isEnRoute &&
        prev.resetGoHere === source.resetGoHere
      ) return prev;
      return source;
    });
  }, []);

  const reconnect = useCallback(() => {
    reconnectRef.current();
  }, []);

  const registerReconnect = useCallback((fn: () => void, reconnecting: boolean) => {
    reconnectRef.current = fn;
    setIsReconnecting(reconnecting);
  }, []);

  const toggleCollapse = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);

  const value = React.useMemo(() => ({
    viewMode,
    setViewMode,
    cameraSource,
    setCameraSource,
    reconnect,
    isReconnecting,
    registerReconnect,
    collapsed,
    toggleCollapse,
  }), [viewMode, setViewMode, cameraSource, setCameraSource, reconnect, isReconnecting, registerReconnect, collapsed, toggleCollapse]);

  return (
    <CameraLayoutContext.Provider value={value}>
      {children}
    </CameraLayoutContext.Provider>
  );
};
